import { map, take, catchError, tap, filter, shareReplay } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject, forkJoin } from 'rxjs';
import { isBefore, addSeconds } from 'date-fns';

import { Injectable } from '@angular/core';
import { Resolve } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

import { moment } from '@app/lib/moment';
import { extractResponseBody } from '@app/lib/utils';

import { ServerApi } from '@app/services/server-api';
import { TimeZoneFormatService } from '@app/admin/fleet-card/shared/billing-tz.service';
import { PublicCardApplicationRouteManager } from './public-card-application-route-manager';
import { FleetCardsFeatureFlagService } from './feature-flags/fleet-cards';

import {
  IAssetVehicleReportingResponse,
  ICustomFieldsServerResponse,
  IFleetCardCompany,
  IFleetCardCompanyServerResponse,
} from '@app/admin/fleet-card/shared/fleet-cards-waitlist/fleet-cards-waitlist.interface';

import { convertCentsToDollars, normaliseMicros } from '@app/admin/fleet-card/shared/utils';
import { BonusRebatesStatus } from '@fleet-cards/overview/bonus-rebates-component/bonus-rebates.const';
import {
  AccountStatus,
  AuthStatus,
  CcmdStatus,
  DISQUALIFIED_REASON,
  OFFBOARD_METADATA,
  PlaidReauthNoticeStatus,
  RISK_PROFILE,
} from '@app/admin/fleet-card/shared/definitions';
import { CARD_PERSONALIZATIONS } from '@app/admin/fleet-card/orders/checkout/checkout-form-config/constants';

interface IBillingServerGetResponse {
  account_overview: IAccountOverviewServerObject;
}

interface IAccountOverviewServerObject {
  max_credit_limit_in_micros: number;
  payment_methods: IPaymentMethodsServerObject[];
  available_credit_in_micros: number;
  available_rebates_in_micros: number;
  pending_rebates_in_micros: number;
  used_credit_in_micros: number;
  payments_in_processing_in_micros: number;
  account_status: AccountStatus;
  billing_cycle_start_date: string;
  billing_cycle_end_date: string;
  payment_due_in_micros: number;
  payment_due_date: string;
  payment_grace_period_date: string;
  current_cycle_due_date: string;
  previous_cycle_due_date: string;
  current_balance_in_micros: number;
  current_payable_balance_in_micros: number;
  billing_timezone: string;
  ccmd_status: CcmdStatus;
  deferred_credit_enabled: boolean;
  plaid_reauth_notice_status: PlaidReauthNoticeStatus;
  risk_profile: RISK_PROFILE;
  offboard_metadata?: IOffboardMetadataServer;
  should_show_cash_advance?: boolean;
  ccmd_available_withdrawal_credit_in_micros?: number;
  max_cash_limit?: number;
  redeemable_reward_amount_in_cents?: number;
  redemption_percentage_in_decimal?: number;
  rewards?: {
    available_amount_in_micros: number;
    earned_amount_in_micros: number;
    redeemed_amount_in_micros: number;
  };
  due_amount_info: IDueAmountInfoServerResponse;
  past_due_amount_info: IDueAmountInfoServerResponse;
  pending_transactions_amount_in_micros?: number;
  previous_cycle_end_time?: string;
  previous_cycle_start_time?: string;
}

export interface IDueAmountInfo {
  totalAmount: number;
  invoices: IInvoiceBreakdown[];
}

interface IInvoiceBreakdown {
  startTime: string;
  endTime: string;
  dueAt: string;
  amount: number;
}

interface IInvoiceBreakdownServerResponse {
  start_time: string;
  end_time: string;
  payment_due_at: string;
  amount_due_in_micros: number;
}

interface IDueAmountInfoServerResponse {
  total_due_amount_in_micros: number;
  invoices_breakdown: IInvoiceBreakdownServerResponse[];
}

// Todo: remove cancel_reasons & disqualified_reason once all the companies are migrated to new offboard metadata
interface IOffboardMetadataServer {
  cancel_code?: string;
  canceled_at?: string;
  disqualified_at?: string;
  cancel_reason?: string;
  cancel_reasons?: string[];
  earliest_reapply_date?: string;
  disqualified_reason?: string;
  disqualified_reasons?: string[];
}

interface IAccountOverviewServerObject {
  max_credit_limit_in_micros: number;
  payment_methods: IPaymentMethodsServerObject[];
  available_credit_in_micros: number;
  used_credit_in_micros: number;
  account_status: AccountStatus;
  billing_cycle_start_date: string;
  billing_cycle_end_date: string;
  payment_due_in_micros: number;
  payment_due_date: string;
  payment_grace_period_date: string;
  current_cycle_due_date: string;
  previous_cycle_due_date: string;
  current_balance_in_micros: number;
  billing_timezone: string;
}

export interface IPaymentMethodServerObject {
  id: string;
  bank_name: string;
  status: string;
  account_number: string;
  plaid_auth_status: AuthStatus;
  default?: boolean;
  payment_method_type?: string;
  virtual_account?: {
    account_number: string,
    routing_number: string,
  }
  created_at: string;
}

export interface IPaymentMethodsServerObject {
  payment_method: IPaymentMethodServerObject;
}

export interface IPaymentMethodObject {
  id: string;
  bankName: string;
  status: string;
  plaidAuthStatus: AuthStatus;
  accountNumber: string;
  isDefault?: boolean;
  isLowBalance?: boolean;
  paymentMethodType?: string;
  virtualAccount?: IVirtualAccount;
  createdAt: string;
}

export interface IVirtualAccount {
  accountNumber: string,
  routingNumber: string,
}

export interface IAccountOverviewObject {
  totalCreditLimit: number;
  availableCredit: number;
  currentBalance: number;
  currentPayableBalance: number;
  paymentsInProcessing: number;
  billingCycleStartDate: string;
  billingCycleEndDate: string;
  dueDate: string;
  graceEndDate: string;
  amountDue: number;
  currentCycleDueDate: string;
  previousCycleDueDate: string;
  previousCycleEndTime: string;
  previousCycleStartTime: string;
  accountStatus: AccountStatus;
  paymentMethods: IPaymentMethodObject[];
  billingTimezone: string;
  ccmdStatus: CcmdStatus;
  plaidReauthNoticeStatus: PlaidReauthNoticeStatus;
  deferredCreditEnabled: boolean;
  riskProfile: RISK_PROFILE;
  cancelReasons?: string[];
  disqualifiedReasons?: string[];
  earliestReapplyDate?: string;
  disqualifiedAt?: string;
  canceledAt?: string;
  shouldShowCashAdvance?: boolean;
  availableWithdrawalCredit?: number;
  maxCashLimit?: number;
  redeemableReward?: number;
  availableRebates?: number;
  pendingRebates?: number;
  rewards?: {
    availableAmount: number;
    earnedAmount: number;
    redeemedAmount: number;
    redemptionPercentage: number;
  };
  dueAmountInfo?: IDueAmountInfo;
  pastDueAmountInfo?: IDueAmountInfo;
  pendingTransactionsAmount?: number;
}

export interface IBilling {
  accountOverview: IAccountOverviewObject;
}

export interface IInactiveStatus {
  isInactiveaccount: boolean;
  isDerogatoryAccount: boolean;
  isCanceledCompanyRequest: boolean;
}

export interface IPotentialSavingsServerResponse {
  potential_savings: IPotentialSavingsServerObject;
}

export interface IPotentialSavingsServerObject {
  annualized_fraud_savings: number;
  annualized_fuel_rebates: number;
  annualized_operational_savings: number;
  contract_info: IContractInfoServerObject;
}

export interface IPotentialSavingsObject {
  annualizedFraudSavings: number;
  annualizedFuelRebates: number;
  annualizedOperationalSavings: number;
  contractInfo: IContractInfoObject;
}

export interface IContractInfoServerObject {
  annual_contract_value: number;
  total_contract_value: number;
  customer_acquisition_date: string;
  reward_percentage_decimal: number;
  reward_type: string;
}

export interface IContractInfoObject {
  annualContractValue: number;
  totalContractValue: number;
  customerAcquisitionDate: string;
  rewardPercentageDecimal: number;
  rewardType: string;
}

export interface IRequestCashAdvance {
  amount: number;
  reason: string;
}

export interface IRequestCashAdvanceParams {
  request_params: IRequestCashAdvance;
  feature_name: string;
}

export interface IRequestCashAdvanceResponse {
  request_params: IRequestCashAdvance,
  error?: string
  feature_name: string,
  request_state: string,
  feature_status: string,
}

interface IBonusRebatesServerResponse {
  enabled: boolean,
  type: 'fuel_gallons',
  unit: 'gallons',
  threshold: number,
  purchase_to_date: number,
  purchase_required: number,
  projected_purchase: number,
  additional_rebates_per_unit_in_cents: number,
  days_left: number,
  current_status: BonusRebatesStatus,
  additional_info?: {
    title: string,
    body: string,
    html?: string,
  },
  additional_info_2?: {
    title: string,
    body: string,
  },
  hide_additional_info: boolean,
  bonus_rebates_earned_in_micros?: number,
  bonus_rebates_pending_in_micros?: number,
  threshold_crossed_popup_dismissed: boolean,
  threshold_effective_till?: string,
  next_threshold?: number,
  in_threshold_transition_phase: boolean,
}

export interface IBonusRebates {
  enabled: boolean,
  type: 'fuel_gallons',
  unit: 'gallons',
  threshold: number,
  purchaseToDate: number,
  purchaseRequired: number,
  projectedPurchase: number,
  additionalRebatesPerUnitInCents: number,
  daysLeft: number,
  currentStatus: BonusRebatesStatus,
  additionalInfo?: {
    title: string,
    body: string,
    html?: string,
  },
  additionalInfo2?: {
    title: string,
    body: string,
  },
  hideAdditionalInfo: boolean,
  bonusRebatesEarnedInMicros: number,
  bonusRebatesPendingInMicros: number,
  thresholdCrossedPopupDismissed: boolean,
  thresholdEffectiveTill?: Date,
  nextThreshold?: number,
  inThresholdTransitionPhase: boolean,
}

@Injectable({ providedIn: 'root' })
export class FleetCardsDataService implements Resolve<[IBilling]> {

  private cacheInvalidationTime: Date;
  private accountUpdateSubject: Subject<any> = new Subject<any>();
  private companyMetadata$: BehaviorSubject<IFleetCardCompany> = new BehaviorSubject<IFleetCardCompany>(null);
  private accountOverview$: BehaviorSubject<any> = new BehaviorSubject<IBilling>(null);
  private accountUpdate$: Observable<any> = new Observable<any>();
  private createCCMDOnGetDisabled: boolean;
  private readonly BASE_URL: string = 'fleetcard/w1';
  private tosSubmitSuccessSubject = new Subject<void>();
  private isFleetCardsAutoDeclineTransactionM1FuelLevelAboveXEnabled: boolean = false;
  private isFleetCardsCaptureAssetInfoOnTransactionsEnabled: boolean = false;
  private isFleetCardsSoftDeclineVslmAndFuelEnabled: boolean = false;

  constructor(
    private api: ServerApi,
    private publicCardApplicationRouteManager: PublicCardApplicationRouteManager,
    private fcFeatureFlagService: FleetCardsFeatureFlagService,
    private timeZoneFormat: TimeZoneFormatService,
  ) {
    this.accountUpdate$ = this.accountUpdateSubject.asObservable();
    this.createCCMDOnGetDisabled = this.fcFeatureFlagService.isCreateCCMDOnGetDisabled();
    this.isFleetCardsAutoDeclineTransactionM1FuelLevelAboveXEnabled =
      this.fcFeatureFlagService.isFleetCardsAutoDeclineTransactionM1FuelLevelAboveXEnabled();
    this.isFleetCardsCaptureAssetInfoOnTransactionsEnabled =
      this.fcFeatureFlagService.isFleetCardsCaptureAssetInfoOnTransactionsEnabled();
    this.isFleetCardsSoftDeclineVslmAndFuelEnabled =
      this.fcFeatureFlagService.isFleetCardsSoftDeclineVslmAndFuelEnabled();
  }

  resolve(): Observable<[IBilling]> {
    return forkJoin([this.getCachedAccountOverview()]);
  }

  submitSuccess$ = this.tosSubmitSuccessSubject.asObservable();

  public notifyTosSubmitSuccess(): void {
    this.tosSubmitSuccessSubject.next();
  }

  public getCachedAccountOverview(): Observable<IBilling> {
    const cachedData: IBilling = this.accountOverview$.getValue();
    if (cachedData) {
      return this.accountOverview$.pipe(take(1));
    } else {
      return this.refreshAccountStatus();
    }
  }

  public createCCMDId(): Observable<IFleetCardCompany> {
    const params: Map<string, boolean> = new Map();
    if (this.publicCardApplicationRouteManager.isPublicCardApplication()) params.set('public_app', true);

    const request: Observable<IFleetCardCompany> = this.api
      .post(`${this.BASE_URL}/company`, params)
      .pipe(
        map(extractResponseBody),
        map((val: { company_card_metadata: IFleetCardCompanyServerResponse }) => val.company_card_metadata),
        map(this.transformCompanyData.bind(this)),
      );

    return request;
  }

  public getCompanyCardMetadataStream(useCache: boolean = true): Observable<IFleetCardCompany> {
    const params: Map<string, boolean> = new Map();
    if (this.publicCardApplicationRouteManager.isPublicCardApplication()) params.set('public_app', true);

    const request: Observable<IFleetCardCompany> = this.api
      .get(`${this.BASE_URL}/company`, params)
      .pipe(
        catchError(this.handleCCMDNotCreated.bind(this)),
        map(extractResponseBody),
        map((val: { company_card_metadata: IFleetCardCompanyServerResponse }) => val.company_card_metadata),
        map(this.transformCompanyData.bind(this)),
        tap((transformedData: IFleetCardCompany) => this.companyMetadata$.next(transformedData)),
      );
    if (isBefore(new Date(), this.cacheInvalidationTime) && useCache) {
      return this.companyMetadata$.pipe(filter<IFleetCardCompany>(Boolean), take(1));
    } else {
      this.companyMetadata$.next(null);
      this.cacheInvalidationTime = addSeconds(new Date(), 5);
      return request;
    }
  }

  public getCompanyPotentialSavings(): Observable<IPotentialSavingsObject> {
    return this.api
      .get(`${this.BASE_URL}/company/potential_savings`)
      .pipe(
        map(extractResponseBody),
        map(this.transformPotentialSavingsData),
      );
  }

  public updateCCMDTermsOfService(): Observable<{}> {
    return this.api.patch(`${this.BASE_URL}/company/terms_of_service`, {
      consented_via: 'banner',
    })
      .pipe(
        map(extractResponseBody),
      );
  }

  public transformPotentialSavingsData(data: IPotentialSavingsServerResponse): IPotentialSavingsObject {
    const contractInfo: IContractInfoObject = {
      annualContractValue: data.potential_savings?.contract_info?.annual_contract_value ?? 0,
      totalContractValue: data.potential_savings?.contract_info?.total_contract_value ?? 0,
      customerAcquisitionDate: data.potential_savings?.contract_info?.customer_acquisition_date ?? '',
      rewardPercentageDecimal: data.potential_savings?.contract_info?.reward_percentage_decimal ?? 0,
      rewardType: data.potential_savings?.contract_info?.reward_type ?? '',
    };

    const potentialSavings: IPotentialSavingsObject = {
      annualizedFraudSavings: data.potential_savings?.annualized_fraud_savings ?? 0,
      annualizedFuelRebates: data.potential_savings?.annualized_fuel_rebates ?? 0,
      annualizedOperationalSavings: data.potential_savings?.annualized_operational_savings ?? 0,
      contractInfo,
    };

    return potentialSavings;
  }


  public getCachedCompanyMetadata(): Observable<IFleetCardCompany> {
    return this.companyMetadata$.pipe(filter<IFleetCardCompany>(Boolean), take(1));
  }

  private handleCCMDNotCreated(error: HttpErrorResponse): Observable<IFleetCardCompany> {
    /** company API will return 400 when CCMD ID has not been created
     * In that case, we trigger a post call to the same endpoint to trigger CCMD creation
     */
    if (error.status === 400) {
      if (this.createCCMDOnGetDisabled) {
        return this.createCCMDId();
      }
    }
  }

  public getBonusRebatesData(): Observable<IBonusRebates> {
    return this.api.get(`${this.BASE_URL}/overview/bonus_rebates`).pipe(
      map(extractResponseBody),
      map(this.transformBonusRebatesResponse),
    );
  }

  public getAccountStatusStream(): Observable<IBilling> {
    return this.accountUpdate$;
  }

  public isInActiveStates(accountOverview: IAccountOverviewObject): IInactiveStatus {
    const isInactiveaccount: boolean = this.isInactiveAccount(accountOverview);
    const isDerogatoryAccount: boolean = this.isDerogatoryAccount(accountOverview);
    const isCanceledCompanyRequest: boolean = this.isCanceledCompanyCall(accountOverview);
    return { isInactiveaccount, isDerogatoryAccount, isCanceledCompanyRequest };
  }

  public isCanceledCompanyCall(accountOverview: IAccountOverviewObject): boolean {
    const ccmdStatus: CcmdStatus = accountOverview?.ccmdStatus;
    const cancelReasons: string[] = accountOverview?.cancelReasons;
    return ccmdStatus === CcmdStatus.CANCELED && cancelReasons.includes(OFFBOARD_METADATA.COMPANY_REQUEST);
  }

  public isInactiveAccount(accountOverview: IAccountOverviewObject): boolean {
    const ccmdStatus: CcmdStatus = accountOverview?.ccmdStatus;
    const cancelReasons: string[] = accountOverview?.cancelReasons;
    return ccmdStatus === CcmdStatus.CANCELED && cancelReasons.includes(OFFBOARD_METADATA.INACTIVITY_AUTO);
  }

  public isDerogatoryAccount(accountOverview: IAccountOverviewObject): boolean {
    const ccmdStatus: CcmdStatus = accountOverview?.ccmdStatus;
    const disqualifiedReasons: string[] = accountOverview?.disqualifiedReasons;
    return ccmdStatus === CcmdStatus.DISQUALIFIED &&
      disqualifiedReasons.some((reason: string) => DISQUALIFIED_REASON.includes(reason));
  }

  public refreshAccountStatus(): Observable<IBilling> {
    const params = new Map<string, string>();
    params.set('invoices_breakdown', 'true');
    const request: Observable<IBilling> = this.api
      .get(`${this.BASE_URL}/invoices/account_overview`, params)
      .pipe(
        map(extractResponseBody),
        map(this.transformData.bind(this)),
      );
    request.subscribe((response: IBilling) => {
      this.accountUpdateSubject.next({ ...response });
      this.accountOverview$.next({ ...response });
    });
    return request;
  }

  private transformData(data: IBillingServerGetResponse): IBilling {
    const paymentMethods: IPaymentMethodObject[] = data.account_overview.payment_methods
      .map((serverObject: IPaymentMethodsServerObject): IPaymentMethodObject => {
        return {
          id: serverObject.payment_method.id,
          bankName: serverObject.payment_method.bank_name,
          status: serverObject.payment_method.status,
          plaidAuthStatus: serverObject.payment_method.plaid_auth_status,
          accountNumber: serverObject.payment_method.account_number,
          isDefault: serverObject.payment_method.default,
          isLowBalance: false,
          createdAt: serverObject.payment_method.created_at,
          paymentMethodType: serverObject.payment_method.payment_method_type,
          ...(serverObject.payment_method.virtual_account && {
            virtualAccount: {
              accountNumber: serverObject.payment_method.virtual_account?.account_number,
              routingNumber: serverObject.payment_method.virtual_account?.routing_number,
            },
          }),
        };
      });
    let dueDate = data.account_overview.payment_due_date;
    if (data.account_overview.payment_due_date) {
      dueDate = moment(dueDate).format('MMM DD, YYYY');
    }

    // Todo: clean up these if-else once all the companies are migrated to new offboard metadata
    let cancelReasons: string[] = [];
    if (data.account_overview?.offboard_metadata?.cancel_reasons) {
      cancelReasons = data.account_overview?.offboard_metadata?.cancel_reasons;
    } else if (data.account_overview?.offboard_metadata?.cancel_reason) {
      cancelReasons = [data.account_overview?.offboard_metadata?.cancel_reason];
    }
    let disqualifiedReasons: string[] = [];
    if (data.account_overview?.offboard_metadata?.disqualified_reasons) {
      disqualifiedReasons = data.account_overview?.offboard_metadata?.disqualified_reasons;
    } else if (data.account_overview?.offboard_metadata?.disqualified_reason) {
      disqualifiedReasons = [data.account_overview?.offboard_metadata?.disqualified_reason];
    }

    const accountOverview: IAccountOverviewObject = {
      totalCreditLimit: normaliseMicros(data.account_overview.max_credit_limit_in_micros),
      availableCredit: normaliseMicros(data.account_overview.available_credit_in_micros),
      currentBalance: normaliseMicros(data.account_overview.current_balance_in_micros),
      currentPayableBalance: normaliseMicros(data.account_overview.current_payable_balance_in_micros),
      paymentsInProcessing: normaliseMicros(data.account_overview.payments_in_processing_in_micros),
      availableRebates: normaliseMicros(data.account_overview.available_rebates_in_micros),
      pendingRebates: normaliseMicros(data.account_overview.pending_rebates_in_micros),
      pendingTransactionsAmount: normaliseMicros(data.account_overview?.pending_transactions_amount_in_micros ?? 0),
      billingCycleStartDate: data.account_overview.billing_cycle_start_date,
      billingCycleEndDate: data.account_overview.billing_cycle_end_date,
      currentCycleDueDate: data.account_overview.current_cycle_due_date,
      previousCycleDueDate: data.account_overview.previous_cycle_due_date,
      previousCycleEndTime: this.timeZoneFormat.formatToTimeZone(data.account_overview.billing_timezone,
        data.account_overview.previous_cycle_end_time, 'MMM DD, YYYY', true),
      previousCycleStartTime: this.timeZoneFormat.formatToTimeZone(data.account_overview.billing_timezone,
        data.account_overview.previous_cycle_start_time, 'MMM DD, YYYY', false),
      dueDate: data.account_overview.payment_due_date,
      accountStatus: data.account_overview.account_status,
      graceEndDate: data.account_overview.payment_grace_period_date,
      amountDue: normaliseMicros(data.account_overview.payment_due_in_micros),
      paymentMethods,
      billingTimezone: data.account_overview.billing_timezone,
      ccmdStatus: data.account_overview.ccmd_status,
      plaidReauthNoticeStatus: data.account_overview.plaid_reauth_notice_status,
      deferredCreditEnabled: data.account_overview?.deferred_credit_enabled,
      riskProfile: data.account_overview?.risk_profile,
      cancelReasons,
      earliestReapplyDate: data.account_overview?.offboard_metadata?.earliest_reapply_date,
      canceledAt: data.account_overview?.offboard_metadata?.canceled_at,
      disqualifiedAt: data.account_overview?.offboard_metadata?.disqualified_at,
      disqualifiedReasons,
      shouldShowCashAdvance: data.account_overview?.should_show_cash_advance,
      availableWithdrawalCredit: normaliseMicros(data.account_overview?.ccmd_available_withdrawal_credit_in_micros),
      maxCashLimit: normaliseMicros(data.account_overview?.max_cash_limit),
      redeemableReward: convertCentsToDollars(data.account_overview?.redeemable_reward_amount_in_cents),
      rewards: {
        availableAmount: normaliseMicros(data.account_overview?.rewards?.available_amount_in_micros),
        earnedAmount: normaliseMicros(data.account_overview?.rewards?.earned_amount_in_micros),
        redeemedAmount: normaliseMicros(data.account_overview?.rewards?.redeemed_amount_in_micros),
        redemptionPercentage: (data.account_overview?.redemption_percentage_in_decimal ?? 0.01) * 100,
      },
      dueAmountInfo: {
        totalAmount: normaliseMicros(data.account_overview.due_amount_info?.total_due_amount_in_micros),
        invoices: data.account_overview.due_amount_info?.invoices_breakdown?.map((dueAmount: IInvoiceBreakdownServerResponse) => {
          return {
            startTime: dueAmount.start_time,
            endTime: dueAmount.end_time,
            dueAt: dueAmount.payment_due_at,
            amount: normaliseMicros(dueAmount.amount_due_in_micros),
          };
        }) || [],
      },
      pastDueAmountInfo: {
        totalAmount: normaliseMicros(data.account_overview.past_due_amount_info?.total_due_amount_in_micros),
        invoices: data.account_overview.past_due_amount_info?.invoices_breakdown?.map(
          (dueAmount: IInvoiceBreakdownServerResponse) => {
            return {
              startTime: dueAmount.start_time,
              endTime: dueAmount.end_time,
              dueAt: dueAmount.payment_due_at,
              amount: normaliseMicros(dueAmount.amount_due_in_micros),
            };
          },
        ) || [],
      },
    };
    return {
      accountOverview,
    };
  }

  private transformCompanyData(data: IFleetCardCompanyServerResponse): IFleetCardCompany {
    if (!data) return;

    const preTxnMetaData = data.transaction_metadata || data.pre_transaction_metadata;
    const assetVehicleReportingSettings: IAssetVehicleReportingResponse =
      data?.pre_transaction_metadata?.asset_vehicle_reporting_settings || data?.transaction_metadata?.asset_vehicle_reporting_settings;

    // TODO : data?.transaction_metadata will be removed with the post txn FF
    const companySpendLimits = data.company_spend_limits;

    const postTxnMetaData = data.post_transaction_metadata;
    const company: IFleetCardCompany = {
      status: data.status,
      billingTimezone: data.billing_timezone,
      ...(data.card_application && {
        cardApplication: {
          id: data.card_application?.id,
          status: data.card_application?.status,
          updatedAt: data.card_application?.updated_at,
          createdAt: data.card_application?.created_at,
          publicApp: data.card_application?.public_app,
          creditApprovedAt: data.card_application?.credit_approved_at,
          expediteShipping: data.card_application?.expedite_shipping,
          partner: data.card_application?.partner,
          submittedAt: data.card_application?.submitted_at,
          appliedByUserName: data.card_application?.applied_by_user_name,
          appliedByUserId: data.card_application?.applied_by_user_id,
          appliedByUserEmailMasked: data.card_application?.applied_by_user_email_masked,
          resendVerifyEmailEligibleAt: data.card_application?.resend_verify_email_eligible_at,
        },
      }),
      allowedSpendCategories: data.allowed_spend_categories,
      settings: {
        ...(data.settings && {
          fraudControls: {
            textAuthStatus: data.settings?.fraud_controls.text_auth_status,
          },
        }),
        vehicleReporting: data.settings.vehicle_reporting,
        vehicleCardSecuritySettings: data.settings.vehicle_card_security_settings,
        fleetUserCardSecuritySettings: data.settings.fleet_user_card_security_settings,
        vehicleReportingReminderLatestTime: data.settings.vehicle_reporting_reminder_latest_time,
        ...(data?.settings?.vehicle_proximity_based_decline_settings &&
          {
            vehicleProximityBasedDeclineSettings: {
              distanceThresholdKm: data?.settings?.vehicle_proximity_based_decline_settings?.distance_threshold_km,
              settingEnabled: data?.settings?.vehicle_proximity_based_decline_settings?.setting_enabled,
            },
          }
        ),
        vehicleLocationMismatchEnabled: data?.settings?.vehicle_location_mismatch_enabled,
        fuelTypeMismatchEnabled: data?.settings?.fuel_type_mismatch_enabled,
        fuelTankLevelMismatchEnabled: data?.settings?.fuel_tank_level_mismatch_enabled,
        ...(
          this.isFleetCardsAutoDeclineTransactionM1FuelLevelAboveXEnabled &&
          data?.settings?.fuel_level_exceeds_limit_settings && {
            fuelLevelExceedsLimit: {
              enabled: data.settings.fuel_level_exceeds_limit_settings.enabled,
              fuelLevelLimit: data.settings.fuel_level_exceeds_limit_settings.fuel_level_limit,
            },
          }
        ),
        softDeclineEnabled: (this.isFleetCardsSoftDeclineVslmAndFuelEnabled &&
          data?.settings?.soft_decline_enabled) ?? false,
      },
      ...(data.shipping_details && {
        shippingDetails: {
          providerTrackingNumber: data.shipping_details?.provider_tracking_number,
          providerShipDate: data.shipping_details?.provider_ship_date,
          hasReceivedCards: data.shipping_details?.has_received_cards,
          providerTrackingUrl: data.shipping_details?.provider_tracking_url,
        },
      }),
      ...(data.payment_method && {
        paymentMethod: {
          id: data.payment_method?.id,
          bankName: data.payment_method?.bank_name,
          status: data.payment_method?.status,
          accountNumber: data.payment_method?.account_number,
          plaidAuthStatus: data.payment_method?.plaid_auth_status,
          isDefault: data.payment_method?.default,
          ...(data.payment_method?.virtual_account && {
            virtualAccount: {
              routingNumber: data.payment_method?.virtual_account.routing_number,
              accountNumber: data.payment_method?.virtual_account.account_number,
            },
          }),
          createdAt: data.payment_method?.created_at,
        },
      }),
      unactivatedCardCount: data.unactivated_card_count,
      approvedCardCount: data.approved_card_count,
      maxCreditLimitInMicros: data.max_credit_limit_in_micros,
      firstTransactionPerformedAt: data.first_transaction_performed_at,
      billingCycle: data.billing_cycle,
      billingDayOfWeek: data.billing_day_of_week,
      hasMixedCardProducts: data.has_mixed_card_products,
      transactionMetadata: {
        vehicleReporting: {
          status: preTxnMetaData?.vehicle_reporting.status,
          acceptAnyVehicleId: preTxnMetaData?.vehicle_reporting.accept_any_vehicle_id,
          manualEntry: preTxnMetaData?.vehicle_reporting.manual_entry,
        },
        odometerReporting: {
          status: preTxnMetaData?.odometer_reporting.status,
          manualEntry: preTxnMetaData?.odometer_reporting.manual_entry,
        },
        customFields: preTxnMetaData?.custom_fields?.map((customField: ICustomFieldsServerResponse) => ({
          id: customField.id,
          status: customField.status,
          name: customField.name,
          valueType: customField.value_type,
          values: customField.values,
        })),
        receiptRequired: postTxnMetaData?.receipt_required?.status,
        ...(
          this.isFleetCardsCaptureAssetInfoOnTransactionsEnabled &&
          assetVehicleReportingSettings && {
            assetVehicleReporting: {
              status: assetVehicleReportingSettings?.status,
              odometerReporting: assetVehicleReportingSettings?.odometer_reporting,
              autoPopulateVGValues: assetVehicleReportingSettings?.auto_populate_vg_values,
              validateReportedValuesInSms: assetVehicleReportingSettings?.input_validation_in_sms,
            },
          }
        ),
      },
      cashAdvancesEnabled: data.cash_advances_enabled,
      cashAdvancesMaxDailyWithdrawalLimit: normaliseMicros(data.cash_advances_max_daily_withdrawal_limit_in_micros),
      cashAdvancesMotiveFee: normaliseMicros(data.cash_advances_motive_fee_in_micros),
      ...(data.card_order_preferences && {
        cardOrderPreferences: {
          eligibleCards: data.card_order_preferences?.eligible_cards,
          embossedCards: data.card_order_preferences?.embossed_cards,
          expediteShipping: data.card_order_preferences?.expedite_shipping,

          // if null, then it means its generic/standard
          cardStyle: data.card_order_preferences?.card_style ? CARD_PERSONALIZATIONS.custom : CARD_PERSONALIZATIONS.standard, 
        },
      }),
      isDeferredRebatesEnabled: data.deferred_customer_rebates_enabled,
      fleetBundleRewardType: data.fleet_bundle_reward_type,
      clearedTransactionsCount: data.cleared_transactions_count,
      autoDeduct: data.auto_deduct,
      cashAdvanceFeatureStatus: data.cash_advance_feature_status,
      containsHighFlaggingVehicles: data.contains_high_flagging_vehicles,
      termsOfService: data.terms_of_service,
      companySpendLimits: {
        dailyLimit:
          companySpendLimits ?.daily_limit_in_cents ? convertCentsToDollars(companySpendLimits?.daily_limit_in_cents) : null,
        monthlyLimit:
          companySpendLimits?.monthly_limit_in_cents ? convertCentsToDollars(companySpendLimits?.monthly_limit_in_cents) : null,
        transactionLimit:
          companySpendLimits?.transaction_limit_in_cents ?
            convertCentsToDollars(companySpendLimits?.transaction_limit_in_cents) : null,
        creditLimit:
          companySpendLimits?.credit_limit_in_cents ? convertCentsToDollars(companySpendLimits?.credit_limit_in_cents) : null,
      },
      singleTransactionUnlock: data.single_transaction_unlock,
    };
    return company;
  }

  public requestCashAdvance(params: IRequestCashAdvanceParams): Observable<any> {
    return this.api.post(`${this.BASE_URL}/company/raise_feature_request`, params)
      .pipe(
        map(extractResponseBody),
      );
  }

  private transformBonusRebatesResponse(response: IBonusRebatesServerResponse): IBonusRebates {
    return {
      enabled: response.enabled,
      type: response.type,
      unit: response.unit,
      threshold: response.threshold,
      purchaseToDate: response.purchase_to_date,
      purchaseRequired: response.purchase_required,
      projectedPurchase: response.projected_purchase,
      additionalRebatesPerUnitInCents: response.additional_rebates_per_unit_in_cents,
      daysLeft: response.days_left,
      currentStatus: response.current_status,
      additionalInfo: response.additional_info ? {
        title: response.additional_info.title,
        body: response.additional_info.body,
        html: response.additional_info.html,
      } : null,
      additionalInfo2: response.additional_info_2 ? {
        title: response.additional_info_2.title,
        body: response.additional_info_2.body,
      } : null,
      hideAdditionalInfo: response.hide_additional_info,
      bonusRebatesEarnedInMicros: normaliseMicros(response.bonus_rebates_earned_in_micros ?? 0),
      bonusRebatesPendingInMicros: normaliseMicros(response.bonus_rebates_pending_in_micros ?? 0),
      thresholdCrossedPopupDismissed: response.threshold_crossed_popup_dismissed,
      thresholdEffectiveTill: response.threshold_effective_till ? new Date(response.threshold_effective_till) : null,
      nextThreshold: response.next_threshold ?? null,
      inThresholdTransitionPhase: response.in_threshold_transition_phase,
    };
  }
}